import { FC, useCallback, useEffect, useMemo } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useLocation, useSearchParams } from "react-router-dom";
import { useRecoilValue, useSetRecoilState } from "recoil";

import { ErrorsDisplayContainer } from "@ryde/controls";
import {
  useApplication,
  useAuthActions,
  useRecaptchaToken,
  useSimpleNavigation,
  useYupValidationResolver,
} from "@ryde/redux";
import {
  AuthApi,
  IApiError,
  ILoginModel,
  isLoggedInAtom,
  isNonProductionNavbarColorFromEnv,
  Log,
  LoginAuthType,
  loginErrorsAtom,
  loginParamsAtom,
  MuiPaletteType,
  normalizeErrors,
  RecaptchaEnterpriseActions,
  resolveLoginTypeParams,
  sessionBootstrapFromBearerAccessToken,
  setAccessToken,
  setRefreshToken,
  SPA_OAUTH_SESSION_QUERY,
  updateTokensAndReconnectSocket,
  verifyHttpOnlySessionActive,
} from "@ryde/shared";

import { LoginPageFormContext } from "./login.page.form.context";
import { LoginPageFormFlow } from "./login.page.form.flow";
import { LoginFormStyled, TitleLineStyled } from "./login.page.styled";
import { getLoginRedirectErrorKey } from "./login.page.utils";
import { RecaptchaEnterprise, yupLoginValidationSchema } from "./parts";
import { getButtonLoginColor } from "./parts/login.parts.styled";
import { useLoginTranslation } from "./use-login-translation";

export const LoginPageForm: FC = () => {
  const { handleUserLogin, setLoginErrors } = useAuthActions();
  const { applicationType, isAdminApp } = useApplication();

  const { t } = useLoginTranslation();
  const serverErrors = useRecoilValue(loginErrorsAtom);
  const [searchParams, setSearchParams] = useSearchParams();
  const setIsLoggedIn = useSetRecoilState(isLoggedInAtom);
  const { getRecaptchaToken } = useRecaptchaToken();
  const location = useLocation();
  // Login route sits outside `QueryParamProvider` — use the URL/env helper, not `useDebugMode`.
  const isNonProductionLoginColors = useMemo(
    () => isNonProductionNavbarColorFromEnv(location.search || location.hash),
    [location.search, location.hash],
  );

  const { navigateToHomeOrLoginReturnUrl } = useSimpleNavigation();

  const resolver = useYupValidationResolver(
    yupLoginValidationSchema,
    "unknown",
  );

  const methods = useForm<ILoginModel>({
    mode: "all",
    reValidateMode: "onChange",
    criteriaMode: "firstError",
    defaultValues: {
      [LoginAuthType.Username]: "",
      [LoginAuthType.Email]: "",
      [LoginAuthType.Phone]: "",
      password: "",
      loginType: LoginAuthType.Username,
      code: "",
    },
    resolver,
  });

  const {
    getValues,
    clearErrors,
    trigger,
    watch,
    formState: { isValid },
  } = methods;

  const setLoginParams = useSetRecoilState(loginParamsAtom);

  useEffect(() => {
    const initLoginParams = async (): Promise<void> => {
      try {
        const paramsResult = await AuthApi.loginParams();

        if (paramsResult?.success) {
          setLoginParams(new Set(paramsResult.data || []));
        } else {
          const apiErrorNormalized = normalizeErrors(paramsResult?.error, t);
          Log.warn(t("errors.LOGIN_ERROR"), apiErrorNormalized);
          setLoginErrors(apiErrorNormalized);
        }
      } catch (error) {
        Log.warn(t("errors.LOGIN_ERROR"), error);
        const errorsNormalized = normalizeErrors(error as IApiError, t);
        setLoginErrors(errorsNormalized);
      }
    };

    initLoginParams();
  }, [setLoginErrors, setLoginParams, t]);

  useEffect(() => {
    const token = searchParams.get("token");
    const refreshToken = searchParams.get("refreshToken");
    const error = searchParams.get("error");
    const oauthSessionMarker = searchParams.get(SPA_OAUTH_SESSION_QUERY);

    if (error) {
      setLoginErrors([t(getLoginRedirectErrorKey(error))]);
      setAccessToken(null);
      setRefreshToken(null);
      setIsLoggedIn(false);
      return;
    }

    if (oauthSessionMarker === "1") {
      const finalizeCookieOAuth = async (): Promise<void> => {
        const ok = await verifyHttpOnlySessionActive();
        if (ok) {
          updateTokensAndReconnectSocket({
            accessToken: null,
            refreshToken: null,
          });
          setIsLoggedIn(true);
          clearErrors();
          navigateToHomeOrLoginReturnUrl();
        } else {
          setLoginErrors([t("errors.authenticationError")]);
        }
        setSearchParams(prev => {
          const next = new URLSearchParams(prev);
          next.delete(SPA_OAUTH_SESSION_QUERY);
          return next;
        });
      };

      void finalizeCookieOAuth();
      return;
    }

    if (!token) {
      Log.debug("Login form: no Google/SAML OAuth callback detected");
      return;
    }

    const applyGoogleOrSamlTokens = async (): Promise<void> => {
      const migrated = await sessionBootstrapFromBearerAccessToken(token);

      if (migrated) {
        Log.debug("OAuth tokens exchanged for HttpOnly session cookies");
        setRefreshToken(null);
      } else {
        Log.debug(
          "Google authorization - [ACCESS TOKEN] set (legacy localStorage)",
        );
        setAccessToken(token);

        if (refreshToken) {
          Log.debug(
            "Google authorization - [REFRESH TOKEN] set (legacy localStorage)",
          );
          setRefreshToken(refreshToken);
        }
      }

      const hasStoredToken = migrated || !!refreshToken;

      if (hasStoredToken || token) {
        setIsLoggedIn(true);
        clearErrors();
        setIsLoggedIn(true);
        navigateToHomeOrLoginReturnUrl();
      }
    };

    void applyGoogleOrSamlTokens();
  }, [
    clearErrors,
    navigateToHomeOrLoginReturnUrl,
    searchParams,
    setIsLoggedIn,
    setLoginErrors,
    setSearchParams,
    t,
  ]);

  const loginTypeWatchedValue = watch("loginType");

  const buttonLoginColor: MuiPaletteType = useMemo(() => {
    return getButtonLoginColor(loginTypeWatchedValue);
  }, [loginTypeWatchedValue]);

  const handleSubmit = useCallback(async (): Promise<void> => {
    const { Username, Email, Phone, password, loginType } = getValues();

    const recaptchaToken = await getRecaptchaToken(
      RecaptchaEnterpriseActions.LOGIN,
    );

    if (!recaptchaToken) {
      return;
    }

    try {
      await trigger();

      if (isValid) {
        Log.debug(
          `form is valid, starting login for ${Username}|${Email}|${Phone} of type ${loginType}`,
        );
        const loginData = resolveLoginTypeParams({
          Username,
          Email,
          Phone,
          loginType,
        });
        await handleUserLogin({ ...loginData, recaptchaToken, password });
      }
    } catch (err) {
      const errorName = t("errors.LOGIN_ERROR");
      Log.error(errorName, err);
      setLoginErrors([`${errorName}: ${(err as Error)?.message}`]);
    }
  }, [
    getRecaptchaToken,
    getValues,
    handleUserLogin,
    isValid,
    setLoginErrors,
    t,
    trigger,
  ]);

  return (
    <FormProvider {...methods}>
      <RecaptchaEnterprise />

      <LoginFormStyled
        data-e2e-mode={applicationType}
        $isNonProductionLoginColors={isNonProductionLoginColors}
      >
        <TitleLineStyled
          $isNonProductionLoginColors={isNonProductionLoginColors}
        >
          <span className="login-title-row">
            <span className="login-title-primary">{t("ui.LOGIN")}</span>
            {isAdminApp ? (
              <span className="login-title-admin">{t("general.ADMIN")}</span>
            ) : null}
          </span>
        </TitleLineStyled>

        <ErrorsDisplayContainer serverErrors={serverErrors} />
        <LoginPageFormContext.Provider
          value={{
            buttonLoginColor,
            isNonProductionLoginColors,
            loginTypeWatchedValue,
            handleSubmit,
          }}
        >
          <LoginPageFormFlow />
        </LoginPageFormContext.Provider>
      </LoginFormStyled>
    </FormProvider>
  );
};
